import { AdminConfiguration } from '@centecomm/admin-config-service';
import { useCurrentUser } from '@centecomm/portal-service-react';
import DynamicTranslatedContent from 'Core/i18n/Components/DynamicTranslatedContent';
import { IconColors } from 'Icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RenderIfVisible from 'react-render-if-visible';
import { Link } from 'react-router-dom';
import { FlexWrapper } from 'Shared/FlexWrapper';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import useComplianceHubEnabled from 'utils/Hooks/LaunchDarkly/useComplianceHubEnabled';

import styles from './DashboardLinks.module.css';
import COIQuickActionModal from './QuickActionModals/COIQuickActionModal';
import HVACQuickActionModal from './QuickActionModals/HVACQuickActionModal';
import SalesQuickActionModal from './QuickActionModals/SalesQuickActionModal';
import { DashboardLink } from './Types/DashboardLink';
import getOOLinks from './utils/getOOLinks';
import getTenantLinks from './utils/getTenantLinks';

const linkTextStyles: React.CSSProperties = {
    margin: '0.5rem 0 0',
    maxWidth: '4rem',
    color: IconColors.White,
    textAlign: 'center',
    fontSize: '.8rem',
};

interface DashboardLinksProps {
    className?: string;
}

function DashboardLinks({ className }: DashboardLinksProps): React.ReactElement {
    const { t } = useTranslation(['dashboardFeature']);
    const { isTenant, hasPermission } = useCurrentUser();
    const { settings } = useSettingsFromState<AdminConfiguration>();
    const hvacComplianceSettings = settings?.hvacCompliance;
    const salesEnabled = settings?.sale?.visible ?? false;
    const [linksForUser, setLinksForUser] = useState<DashboardLink[]>([]);
    const [hvacEnabled, setHvacEnabled] = useState<boolean>(false);
    const [coiEnabled, setCOIEnabled] = useState<boolean>(false);
    const complianceHubEnabled = useComplianceHubEnabled();

    useEffect(() => {
        if (isTenant) {
            setLinksForUser(getTenantLinks(hasPermission, settings, complianceHubEnabled));

            if (settings?.landingPage?.submitHvacTenant ?? true) {
                setHvacEnabled(true);
            }
        } else {
            setLinksForUser(getOOLinks(hasPermission, settings, complianceHubEnabled));

            if (settings?.landingPage?.viewHavacOwnerOperator ?? true) {
                setHvacEnabled(true);
            }
        }

        setCOIEnabled(
            (settings?.landingPage?.submitInsuranceTenant ?? true) && (settings?.insurance?.visible ?? true) && isTenant,
        );
    }, [isTenant, settings]);

    return (
        <>
            <FlexWrapper align="start" justify="center" wrap style={{ margin: '2rem 0' }} className={className}>
                {linksForUser.map(({ Icon, text, url }) => (
                    <Link
                        key={`round-link-item-${url}-${text.replace(' ', '').toLowerCase()}`}
                        to={url}
                        style={{ margin: '0.5rem' }}
                    >
                        <FlexWrapper align="center" justify="center" column className={styles.BubbleLink}>
                            <Icon
                                aspect="3rem"
                                color={IconColors.White}
                                style={{
                                    strokeWidth: '3px',
                                }}
                            />
                            <RenderIfVisible defaultHeight={50}>
                                <p style={linkTextStyles}>
                                    <DynamicTranslatedContent content={text} showLoadingState>
                                        {({ translatedContent }) => <>{translatedContent}</>}
                                    </DynamicTranslatedContent>
                                </p>
                            </RenderIfVisible>
                        </FlexWrapper>
                    </Link>
                ))}
                {/* Can abstract this logic more if we add more buttons like this */}
                {hvacComplianceSettings?.visible && hvacEnabled && <HVACQuickActionModal />}
                {salesEnabled && <SalesQuickActionModal />}
                {coiEnabled && <COIQuickActionModal />}
            </FlexWrapper>
        </>
    );
}

export default DashboardLinks;
